import React, { useEffect, useState } from 'react';

import { useSnackbar } from 'notistack';

import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
  alpha,
} from '@mui/material';

import {
  useCreateCustomerServiceRep,
  useUpdateCustomerServiceRep,
} from '~/hooks/apis/loyalty/useCustomerServiceRepMutations';

import type { CustomerServiceRep } from '~/services/LuxLoyaltyCustomerServiceRepService';

interface Props {
  open: boolean;
  onClose: () => void;
  /** Provide to edit; omit to create. */
  rep?: CustomerServiceRep;
  /** Existing emails in the configurator. Used to block duplicates client-side. */
  existingEmails: ReadonlyArray<string>;
}

interface FormState {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
}

const EMPTY_STATE: FormState = { firstName: '', lastName: '', email: '', phone: '' };

/** Loose RFC-style check — final validation happens server-side. */
function isLikelyEmail(value: string): boolean {
  return /.+@.+\..+/.test(value);
}

/**
 * Mirrors the backend validation:
 *   regex(/^\+[1-9]\d{0,3}[\d\s-]{4,}$/, 'Phone must start with a country
 *   code, e.g. +61 400 000 000')
 * Numbers stored here are displayed to customers verbatim, so the format
 * needs to match the customer-facing display.
 */
const PHONE_REGEX = /^\+[1-9]\d{0,3}[\d\s-]{4,}$/;
const PHONE_HINT =
  'Use the same format the customer will see — start with +country code, e.g. +61 400 000 000.';
function isValidPhone(value: string): boolean {
  return PHONE_REGEX.test(value);
}

/**
 * Add or edit dialog for customer service reps. Validates required fields and
 * client-side email uniqueness; defers final validation to the backend (409 ->
 * snackbar).
 */
export default function CustomerServiceRepFormDialog({ open, onClose, rep, existingEmails }: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const isEdit = !!rep;

  const [state, setState] = useState<FormState>(EMPTY_STATE);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (!open) {
      return;
    }
    setSubmitted(false);
    setState(
      rep
        ? { firstName: rep.firstName, lastName: rep.lastName, email: rep.email, phone: rep.phone }
        : EMPTY_STATE,
    );
  }, [open, rep]);

  const trimmed = {
    firstName: state.firstName.trim(),
    lastName: state.lastName.trim(),
    email: state.email.trim(),
    phone: state.phone.trim(),
  };

  // Field-level error helpers. Only surface once the admin has tried to submit
  // or has typed-then-cleared a field.
  const errors = {
    firstName: !trimmed.firstName ? 'First name is required.' : null,
    lastName: !trimmed.lastName ? 'Last name is required.' : null,
    email: (() => {
      if (!trimmed.email) {
        return 'Email is required.';
      }
      if (!isLikelyEmail(trimmed.email)) {
        return 'Enter a valid email address.';
      }
      const duplicate = existingEmails
        .filter((existing) => existing.toLowerCase() !== rep?.email.toLowerCase())
        .some((existing) => existing.toLowerCase() === trimmed.email.toLowerCase());
      if (duplicate) {
        return 'A rep with this email already exists.';
      }
      return null;
    })(),
    phone: !trimmed.phone
      ? 'Phone is required.'
      : !isValidPhone(trimmed.phone)
        ? 'Phone must start with a country code, e.g. +61 400 000 000.'
        : null,
  };

  const hasErrors = Object.values(errors).some(Boolean);

  const createMutation = useCreateCustomerServiceRep({
    onSuccess: () => {
      enqueueSnackbar(`Created rep ${trimmed.firstName} ${trimmed.lastName}`, { variant: 'success' });
      onClose();
    },
    onError: (error) => {
      enqueueSnackbar(`Failed to create rep: ${error.message}`, { variant: 'error' });
    },
  });

  const updateMutation = useUpdateCustomerServiceRep({
    onSuccess: () => {
      enqueueSnackbar(`Updated rep ${trimmed.firstName} ${trimmed.lastName}`, { variant: 'success' });
      onClose();
    },
    onError: (error) => {
      enqueueSnackbar(`Failed to update rep: ${error.message}`, { variant: 'error' });
    },
  });

  const isSubmitting = createMutation.isPending || updateMutation.isPending;

  const handleSubmit = () => {
    setSubmitted(true);
    if (hasErrors) {
      return;
    }
    if (isEdit && rep) {
      updateMutation.mutate({ id: rep.id, body: trimmed });
    } else {
      createMutation.mutate(trimmed);
    }
  };

  return (
    <Dialog open={open} onClose={isSubmitting ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isEdit ? 'Edit representative' : 'Add representative'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} mt={1}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label="First name"
              value={state.firstName}
              onChange={(e) => setState({ ...state, firstName: e.target.value })}
              required
              fullWidth
              error={submitted && !!errors.firstName}
              helperText={submitted ? errors.firstName ?? ' ' : ' '}
              disabled={isSubmitting}
            />
            <TextField
              label="Last name"
              value={state.lastName}
              onChange={(e) => setState({ ...state, lastName: e.target.value })}
              required
              fullWidth
              error={submitted && !!errors.lastName}
              helperText={submitted ? errors.lastName ?? ' ' : ' '}
              disabled={isSubmitting}
            />
          </Stack>
          <TextField
            label="Email"
            value={state.email}
            onChange={(e) => setState({ ...state, email: e.target.value })}
            required
            fullWidth
            type="email"
            error={submitted && !!errors.email}
            helperText={submitted ? errors.email ?? ' ' : ' '}
            disabled={isSubmitting}
          />
          <Box>
            <Box
              sx={(theme) => ({
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1,
                px: 1.25,
                py: 0.75,
                mb: 1,
                borderRadius: 1.5,
                bgcolor: alpha(theme.palette.info.main, 0.08),
                border: `1px solid ${alpha(theme.palette.info.main, 0.25)}`,
                color: theme.palette.info.dark,
              })}
            >
              <InfoOutlinedIcon fontSize="small" sx={{ mt: '1px' }} />
              <Typography variant="caption" sx={{ lineHeight: 1.4 }}>
                {PHONE_HINT} This is shown to customers exactly as entered.
              </Typography>
            </Box>
            <TextField
              label="Phone"
              value={state.phone}
              onChange={(e) => setState({ ...state, phone: e.target.value })}
              required
              fullWidth
              placeholder="+61 400 000 000"
              error={submitted && !!errors.phone}
              helperText={submitted ? errors.phone ?? ' ' : ' '}
              disabled={isSubmitting}
            />
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="text" onClick={onClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          disabled={isSubmitting}
          startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {isEdit ? 'Save changes' : 'Add representative'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
